import { Injectable } from '@angular/core';
import { EventBusService } from '@app/core/services/event-bus/event-bus.service';
import {
  AccountStatementGQL,
  AccountStatementQuery,
  TemplateOutput,
} from '@app/generated/graphql';
import { Apollo } from 'apollo-angular';
import * as Sentry from '@sentry/browser';
import { StatementGenerationService } from './statement-generation.service';
import { ApolloQueryResult } from '@apollo/client/core';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { ActiveBrowserTabService } from '@app/core/services/active-browser-tab/active-browser-tab.service';

@Injectable({
  providedIn: 'root',
})
export class StatementDownloadService {
  constructor(
    private eventBus: EventBusService,
    private apollo: Apollo,
    private accountStatementGQL: AccountStatementGQL,
    private statementGenerationService: StatementGenerationService,
    private activeBrowserTabService: ActiveBrowserTabService,
  ) {}

  public setupStatementDownload(): void {
    const eventBus$ = this.getAccountStatementIdStream();

    eventBus$
      .pipe(
        switchMap((id: string) => {
          return this.fetchAccountStatement(id);
        }),
        map((res: ApolloQueryResult<AccountStatementQuery>) => {
          return this.extractTemplateOutput(res);
        }),
        tap((templateOutput: TemplateOutput) => {
          this.handleTemplateOutput(templateOutput);
          this.stopLoading();
        }),
        catchError(this.handleError),
      )
      .subscribe();
  }

  private stopLoading(): void {
    this.statementGenerationService.completeGeneration();
  }

  private getAccountStatementIdStream(): Observable<string> {
    return this.eventBus
      .getChannel('RefetchAccountStatement', false)
      .pipe(filter((id: string) => typeof id === 'string' && id.length > 0));
  }

  private fetchAccountStatement(
    id: string,
  ): Observable<ApolloQueryResult<AccountStatementQuery>> {
    return this.apollo.query<AccountStatementQuery>({
      query: this.accountStatementGQL.document,
      variables: { id },
    });
  }

  private extractTemplateOutput(
    res: ApolloQueryResult<AccountStatementQuery>,
  ): TemplateOutput {
    return res.data.accountStatement.templateOutput;
  }

  private handleTemplateOutput(templateOutput: TemplateOutput): void {
    if (templateOutput?.outputFiles?.length > 0) {
      this.downloadFile(templateOutput);
    }
  }

  private handleError(error: any): Observable<null> {
    console.error('Error fetching account statement:', error);
    Sentry.captureException(error);
    this.stopLoading();
    return of(null);
  }

  private downloadFile(templateOutput: TemplateOutput): void {
    const outputFile = templateOutput?.outputFiles[0];
    const isActiveTab: boolean = this.activeBrowserTabService.isTabActive;

    if (outputFile && isActiveTab) {
      location.href = outputFile.v2Url;
    }
  }
}
